import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import PalmNFCIcon from '../components/icons/PalmNFCIcon';
import { mockUser } from '../mockDataPalmPay';
import { useAuthContext } from '../contexts/AuthContext';
import { getDashboard, registerPalm } from '../api/palm';
import { verifyPalmReceive } from '../api/palm';
import { transferPalm } from '../api/transfer';

const PalmScan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { type, recipient, amount, note, returnTo } = location.state || {};
  const { user } = useAuthContext();
  const [socket, setSocket] = useState(null);
  const [deviceConnected, setDeviceConnected] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [isConnecting, setIsConnecting] = useState(false);
  const [scanStatus, setScanStatus] = useState('scanning'); // 'scanning', 'success', 'failed'
  const [scanProgress, setScanProgress] = useState(0);
  const [deductionComplete, setDeductionComplete] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [sender, setSender] = useState(null);
  const imgRef = useRef(null);
  const maxReconnectAttempts = 3;
  const [socketMessage, setSocketMessage] = useState('');
  const [error, setError] = useState(null);
  useEffect(() => {
    let isMounted = true;
    const connectWebSocket = () => {
      try {
        const ws = new WebSocket("ws://localhost:8188/video");
        setSocket(ws);

        ws.onopen = () => {
          if (isMounted) {
            setDeviceConnected(true)
            setReconnectAttempts(0);
            setIsConnecting(false)
          }
        };

        ws.onmessage = async (event) => {
          const data = JSON.parse(event.data);
          if (imgRef.current) {
            imgRef.current.src = data.frame || '';
          }

          if (data.info) {
            setSocketMessage(data.info);
          }

          // Handle comparison result from WebSocket
          if (data.info) {
            const infoLower = data.info.toLowerCase();
            
            if (infoLower.includes("completed") && data.palmId) {
              console.log("Palm match detected from WebSocket", data);
              await verifyPalmAgainstDatabase(user?.id, data.palmId, amount);
            } else if (infoLower.includes("comparison completed") && !data.palmId) {
              // Comparison completed but no palmId found - this is a failure
              console.log("Palm scan failed - no match found", data);
              setScanStatus('failed');
              setError('Palm not recognized. Please try again.');
            } else if (infoLower.includes("failed") || infoLower.includes("error") || infoLower.includes("no match")) {
              setScanStatus('failed');
              setError('Palm authentication failed. Please try again.');
            }
          }
        };

        ws.onerror = (err) => {
          console.error('WebSocket error:', err);
          if (isMounted) {
            setDeviceConnected(false)
            setIsConnecting(true)
          }
        };

        ws.onclose = (event) => {
          if (isMounted) {
            if (event.code !== 1000 && reconnectAttempts < maxReconnectAttempts) {
              setReconnectAttempts((prev) => prev + 1);
              setTimeout(() => {
                if (isMounted) {
                  connectWebSocket();
                }
              }, 2000);
            }
          }
        };
      } catch (err) {
        console.log(err)
      }
    };
    connectWebSocket();
  }, [scanStatus, user?.left_palm_hash, user?.right_palm_hash]);

  useEffect(() => {
    if (socket && socket.readyState === WebSocket.OPEN && isScanning) {
      console.log("Sending command to compare palm vein");
      socket.send(JSON.stringify({ command: 'compare_palm_vein' }));
    }
  }, [socket, isScanning]);

  // // Simulate palm scanning
  // useEffect(() => {
  //   if (scanStatus === 'scanning') {
  //     const interval = setInterval(() => {
  //       setScanProgress(prev => {
  //         if (prev >= 100) {
  //           clearInterval(interval);
  //           // Simulate successful authentication
  //           setTimeout(() => {
  //             setScanStatus('success');
  //             // Simulate wallet deduction
  //             setTimeout(() => {
  //               setDeductionComplete(true);
  //             }, 1000);
  //           }, 500);
  //           return 100;
  //         }
  //         return prev + 5;
  //       });
  //     }, 100);

  //     return () => clearInterval(interval);
  //   }
  // }, [scanStatus]);

  const verifyPalmAgainstDatabase = async (_userId, palmId, amount) => {
    try {
      if (!amount || parseFloat(amount) <= 0) {
        setScanStatus('failed');
        setError('Please enter a valid amount.');
        return;
      }
      if (!user?.id) {
        setScanStatus('failed');
        setError('Receiver not found. Please log in.');
        return;
      }
      const payRes = await transferPalm({
        palmId,
        receiverId: user?.id,
        amount: parseFloat(amount),
      });

      if (payRes.success) {
        setScanStatus('success');
        setDeductionComplete(true);
        setSender(payRes.payerName || 'Unknown');
      } else {
        setScanStatus('failed');
        setError(payRes.error || 'Payment failed. Please try again.');
      }
    } catch (apiError) {
      const errorMessage = apiError.response?.data?.error || apiError.response?.data?.message || apiError.message || 'Payment failed. Please try again.';
      setScanStatus('failed');
      setError(errorMessage);
    }
  };

  const handleComplete = () => {
    if (returnTo) {
      navigate(returnTo, {
        state: {
          success: true,
          message: `Request sent successfully to ${recipient}`
        }
      });
    } else {
      navigate('/home');
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleStartScan = () => {
    setIsScanning(true);
    setScanProgress(0);
    // Send command if socket is already open
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ command: 'compare_palm_vein' }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A0F1F] via-[#1a1f3a] to-[#0A0F1F] text-white flex flex-col">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden opacity-20">
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#586BFF] rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#9B62FF] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Header */}
      <div className="relative z-10 px-6 py-6 flex items-center gap-4">
        <button onClick={handleCancel} className="p-2 hover:bg-white/10 rounded-full transition-colors">
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div>
          <h1 className="text-xl font-bold">Palm Authentication</h1>
          <p className="text-sm text-white/60">Verify your identity</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6">
        <AnimatePresence mode="sync">
          {scanStatus === 'scanning' && (
            <motion.div
              key="scanning"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="text-center"
            >
              {/* Scanning Area */}
              <div className="relative mb-8">
                <motion.div
                  className="w-full aspect-[4/3] rounded-xl border-2 border-dashed border-white/30 bg-white/5 flex items-center justify-center overflow-hidden"
                  animate={{
                    borderColor: isScanning ? ['rgba(255,255,255,0.3)', 'rgba(100,232,255,0.8)', 'rgba(255,255,255,0.3)'] : 'rgba(255,255,255,0.3)'
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <img
                    ref={imgRef}
                    alt=""
                    className="w-full h-full object-cover"
                  />

                  {/* Scanning Beam */}
                  {isScanning && (
                    <motion.div
                      className="absolute left-0 right-0 h-8 bg-gradient-to-b from-transparent via-[#64E8FF]/50 to-transparent"
                      animate={{
                        top: ['0%', '100%']
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'linear'
                      }}
                    />
                  )}

                  {/* Corner Brackets */}
                  <div className="absolute top-4 left-4 w-6 h-6 border-l-2 border-t-2 border-[#64E8FF]" />
                  <div className="absolute top-4 right-4 w-6 h-6 border-r-2 border-t-2 border-[#64E8FF]" />
                  <div className="absolute bottom-4 left-4 w-6 h-6 border-l-2 border-b-2 border-[#64E8FF]" />
                  <div className="absolute bottom-4 right-4 w-6 h-6 border-r-2 border-b-2 border-[#64E8FF]" />
                </motion.div>

                {/* Device Active Badge */}
                {deviceConnected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-green-500 rounded-full flex items-center gap-2"
                  >
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    <span className="text-white text-xs font-semibold">Device Active</span>
                  </motion.div>
                )}
              </div>

              {/* Status */}
              {!isScanning ? (
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold mb-2">Ready to Scan</h2>
                  <p className="text-white/80 text-sm mb-4">
                    Place your palm flat on the scanner when ready
                  </p>
                  <Button
                    onClick={handleStartScan}
                    disabled={!deviceConnected}
                    className="w-full py-4 bg-gradient-to-r from-[#586BFF] to-[#64E8FF] hover:from-[#4a5ceb] hover:to-[#5ad4eb] text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deviceConnected ? 'Scan Palm' : 'Connecting...'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4 w-full max-w-md mx-auto">
                  <h2 className="text-2xl font-bold mb-2">Scanning Palm...</h2>
                  <p className="text-white/80 text-sm mb-4">
                    {socketMessage || "Place your palm flat on the scanner when ready"}
                  </p>
                  {/* Progress Bar */}
                  <div className="bg-white/10 rounded-full h-2 overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-[#586BFF] to-[#64E8FF]"
                      initial={{ width: 0 }}
                      animate={{ width: `${scanProgress}%` }}
                    />
                  </div>

                  {/* Progress Text */}
                  <div className="text-center">
                    <p className="text-white font-semibold text-lg mb-1">{scanProgress}%</p>
                    <p className="text-white/60 text-sm">
                      {scanProgress < 30 && 'Initializing palm scanner...'}
                      {scanProgress >= 30 && scanProgress < 60 && 'Capturing palm print...'}
                      {scanProgress >= 60 && scanProgress < 90 && 'Analyzing biometric data...'}
                      {scanProgress >= 90 && 'Almost done...'}
                    </p>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {scanStatus === 'success' && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              {/* Success Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200 }}
                className="w-32 h-32 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center"
              >
                <CheckCircle2 className="w-20 h-20 text-green-400" />
              </motion.div>

              <h2 className="text-2xl font-bold mb-2">Authentication Successful!</h2>
              <p className="text-white/60 mb-8">Your palm has been verified</p>

              {/* Transaction Details */}
              <Card className="bg-white/10 backdrop-blur-sm border-white/20 p-6 mb-6 text-left max-w-md mx-auto">
                <h3 className="text-sm font-semibold text-white/60 mb-4">Request Details</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-white/80">To:</span>
                    <span className="font-semibold">{user.username}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/80">Amount:</span>
                    <span className="font-bold text-[#64E8FF] text-xl">₹{amount}</span>
                  </div>
                  {note && (
                    <div className="flex justify-between">
                      <span className="text-white/80">Note:</span>
                      <span className="font-medium">{note}</span>
                    </div>
                  )}
                  <div className="pt-3 border-t border-white/10">
                    <div className="flex justify-between">
                      <span className="text-white/80">Authenticated by:</span>
                      <span className="font-semibold">{sender || user?.username || 'Unknown'}</span>
                    </div>
                  </div>
                </div>
              </Card>

              {deductionComplete && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6"
                >
                  <div className="flex items-center justify-center gap-2 text-green-400">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="text-sm font-semibold">Request sent successfully</span>
                  </div>
                </motion.div>
              )}

              <Button
                onClick={handleComplete}
                className="w-full max-w-md bg-gradient-to-r from-[#586BFF] to-[#9B62FF] hover:from-[#4a5ceb] hover:to-[#8a51eb] h-14 text-lg font-semibold"
              >
                Done
              </Button>
            </motion.div>
          )}

          {scanStatus === 'failed' && (
            <motion.div
              key="failed"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200 }}
                className="w-32 h-32 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center"
              >
                <XCircle className="w-20 h-20 text-red-400" />
              </motion.div>

              <h2 className="text-2xl font-bold mb-2">{error || 'Authentication Failed'}</h2>
              <p className="text-white/60 mb-8">Please try again</p>

              <div className="space-y-3 max-w-md mx-auto">
                <Button
                  onClick={() => {
                    setScanStatus('scanning');
                    setScanProgress(0);
                    setIsScanning(false);
                    setError(null);
                    setSender(null);
                  }}
                  className="w-full bg-gradient-to-r from-[#586BFF] to-[#9B62FF] h-12"
                >
                  Try Again
                </Button>
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer Info */}
      <div className="relative z-10 px-6 py-6 text-center">
        <div className="flex items-center justify-center gap-2 text-xs text-white/40">
          <AlertCircle className="w-4 h-4" />
          <span>Secure palm biometric authentication</span>
        </div>
      </div>
    </div>
  );
};

export default PalmScan;
